import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import type { IDeleteKBResponse } from 'features/chat/agent-chat-module/types/kb.service.type';
import { useDeleteKbById } from 'features/chat/hooks/use-kb';
import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { getApiErrorDetail } from 'utils/api-error';

type KbDeleteDialogProps = {
  projectKey: string;
  kbId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const isDeleteSuccessful = (response: IDeleteKBResponse) => {
  if (response.is_success === false || response.success === false) {
    return false;
  }

  return response.is_success === true || response.success === true || response.is_success === undefined;
};

export function KbDeleteDialog({ projectKey, kbId, open, onOpenChange }: KbDeleteDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { mutateAsync, isPending } = useDeleteKbById();

  const handleConfirm = async () => {
    if (!projectKey) {
      toast({
        title: t('DELETE_FAILED') || 'Delete failed',
        description: t('MISSING_PROJECT_KEY') || 'Project key is missing.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const response = await mutateAsync({
        kb_id: kbId,
        project_key: projectKey,
      });

      if (!isDeleteSuccessful(response)) {
        throw new Error(
          typeof response.error === 'string'
            ? response.error
            : response.detail || response.message || t('FAILED_TO_DELETE') || 'Failed to delete content.'
        );
      }

      toast({
        variant: 'success',
        title: t('DELETED') || 'Content deleted',
        description: t('DELETED_SUCCESSFULLY') || 'The knowledge base item was deleted successfully.',
      });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: t('DELETE_FAILED') || 'Delete failed',
        description: getApiErrorDetail(error) ?? (t('PLEASE_TRY_AGAIN') || 'Please try again.'),
        variant: 'destructive',
      });
    }
  };

  return (
    <ConfirmationModal
      open={open}
      onOpenChange={onOpenChange}
      title={t('DELETE') || 'Delete'}
      description={
        t('DELETE_CONFIRMATION_DESCRIPTION') ||
        'Are you sure you want to delete? This action cannot be undone.'
      }
      onConfirm={handleConfirm}
      confirmText={t('DELETE') || 'Delete'}
      cancelText={t('CANCEL') || 'Cancel'}
      preventAutoClose
      isLoading={isPending}
      confirmVariant="delete"
    />
  );
}
